import React, { useState } from "react";
import {
  View,
  StyleSheet,
  TouchableOpacity,
  ActivityIndicator,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { TTemplate, TCropCoords } from "../../../../../../utils/types";
import { NormalText } from "../../../../../common/native";
import Template from "../../template/Template";
import { TSideBySideProcessedImage } from "../../../../../../services/api-clinic";

type TemplateCarouselProps = {
  templatesData: {
    template: TTemplate;
    afterCroppedImage: string;
    beforeCroppedImage: string;
    afterOriginalImage: string;
    beforeOriginalImage: string;
    afterDimensions: { width: number; height: number };
    beforeDimensions: { width: number; height: number };
    afterCropCoords: TCropCoords;
    beforeCropCoords: TCropCoords;
  }[];
  handleSetAfterCroppedImage: (
    image: string,
    index: number,
    cropCoords?: TCropCoords,
    selectedImage?: TSideBySideProcessedImage,
    rotationDegrees?: number,
  ) => void;
  handleSetBeforeCroppedImage: (
    image: string,
    index: number,
    cropCoords?: TCropCoords,
    selectedImage?: TSideBySideProcessedImage,
    rotationDegrees?: number,
  ) => void;
  handleSetBothCroppedImages: (
    beforeImage: string,
    afterImage: string,
    index: number,
    beforeCropCoords: TCropCoords,
    afterCropCoords: TCropCoords,
    beforeRotationDegrees?: number,
    afterRotationDegrees?: number,
  ) => void;
  handleSetTemplatesDataTemplate: any;
  uploadedImages: { preOp: Blob[]; postOp: Blob[] };
  processedImageUrls?: { preOp: string[]; postOp: string[] };
  unpairedBeforeImages?: TSideBySideProcessedImage[];
  unpairedAfterImages?: TSideBySideProcessedImage[];
  beforeImageOptions: TSideBySideProcessedImage[];
  afterImageOptions: TSideBySideProcessedImage[];
  onPairUnpairedImages?: (
    beforeImage: TSideBySideProcessedImage,
    afterImage: TSideBySideProcessedImage,
  ) => void;
  onSlideChange?: (index: number) => void;
  initialSlideIndex?: number;
  headerContent?: React.ReactNode;
  floatingSideContent?: React.ReactNode;
  tipsEnabled?: boolean;
};

const TemplateCarousel: React.FC<TemplateCarouselProps> = ({
  templatesData,
  uploadedImages,
  processedImageUrls = { preOp: [], postOp: [] },
  unpairedBeforeImages = [],
  unpairedAfterImages = [],
  beforeImageOptions,
  afterImageOptions,
  onPairUnpairedImages,
  handleSetAfterCroppedImage,
  handleSetBeforeCroppedImage,
  handleSetBothCroppedImages,
  handleSetTemplatesDataTemplate,
  onSlideChange,
  initialSlideIndex = 0,
  headerContent,
  floatingSideContent,
  tipsEnabled = false,
}) => {
  const [currentTemplateIndex, setCurrentTemplateIndex] = useState(() =>
    Math.max(0, Math.min(initialSlideIndex, templatesData.length - 1)),
  );

  if (!templatesData || templatesData.length === 0)
    return (
      <ActivityIndicator
        size="large"
        color={"#00b7f4"}
        style={{ marginHorizontal: "auto", marginTop: 50 }}
      />
    );

  const goToNext = () => {
    const nextIndex =
      currentTemplateIndex === templatesData.length - 1
        ? 0
        : currentTemplateIndex + 1;
    setCurrentTemplateIndex(nextIndex);
    if (onSlideChange) {
      onSlideChange(nextIndex);
    }
  };

  const goToPrevious = () => {
    const prevIndex =
      currentTemplateIndex === 0
        ? templatesData.length - 1
        : currentTemplateIndex - 1;
    setCurrentTemplateIndex(prevIndex);
    if (onSlideChange) {
      onSlideChange(prevIndex);
    }
  };

  const currentTemplate = templatesData[currentTemplateIndex];

  const getImageSource = (index: number, type: "preOp" | "postOp") => {
    const processed = processedImageUrls?.[type]?.[index];
    const uploaded = uploadedImages?.[type]?.[index];

    if (processed) {
      console.log(
        `[TemplateCarousel] Using PROCESSED ${type} image at index ${index}:`,
        processed,
      );
      return { uri: processed };
    }

    if (uploaded instanceof Blob) {
      const url = URL.createObjectURL(uploaded);
      console.log(
        `[TemplateCarousel] Using UPLOADED ${type} image at index ${index}:`,
        url,
      );
      return { uri: url };
    }

    console.warn(
      `[TemplateCarousel] No valid ${type} image found at index ${index}`,
    );
    return null;
  };

  // console.log("🧩 TemplateCarousel - Current Template", {
  //   index: currentTemplateIndex,
  //   template: currentTemplate.template,
  //   beforeCroppedImage: currentTemplate.beforeCroppedImage,
  //   afterCroppedImage: currentTemplate.afterCroppedImage,
  // });

  return (
    <View style={styles.carouselContainer}>
      <View style={styles.headerRow}>
        <View style={styles.headerSide} />
        <NormalText
          text={`${currentTemplateIndex + 1} of ${templatesData.length}`}
          style={styles.pageIndicator}
        />
        {headerContent}
      </View>
      {/* Left/Right navigation controls */}
      <View style={styles.carouselControls}>
        <TouchableOpacity
          onPress={goToPrevious}
          disabled={templatesData.length <= 1}
        >
          <Ionicons name="chevron-back" size={30} color="#C4C6CD" />
        </TouchableOpacity>

        <View style={styles.templateContainer}>
          <Template
            template={currentTemplate.template}
            beforeImage={currentTemplate.beforeCroppedImage}
            afterImage={currentTemplate.afterCroppedImage}
            setTemplate={(updaterFn: any) =>
              handleSetTemplatesDataTemplate(updaterFn, currentTemplateIndex)
            }
            displayMode={false}
            beforeCropCoords={currentTemplate.beforeCropCoords}
            afterCropCoords={currentTemplate.afterCropCoords}
            swapImagesModalData={{
              handleSetAfterCroppedImage,
              handleSetBeforeCroppedImage,
              handleSetBothCroppedImages,
              templatesData: templatesData,
              uploadedImages: uploadedImages,
              currentTemplateIndex,
              processedImageUrls: processedImageUrls,
              beforeImageOptions,
              afterImageOptions,
              unpairedBeforeImages,
              unpairedAfterImages,
              onPairUnpairedImages,
            }}
            tipsEnabled={tipsEnabled}
          />

          {floatingSideContent ? (
            <View style={styles.floatingSideContent}>
              {floatingSideContent}
            </View>
          ) : null}
        </View>

        <TouchableOpacity
          onPress={goToNext}
          disabled={templatesData.length <= 1}
        >
          <Ionicons name="chevron-forward" size={30} color="#C4C6CD" />
        </TouchableOpacity>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  carouselContainer: {
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
  },
  carouselControls: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
  },
  templateContainer: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
  },
  floatingSideContent: {
    position: "absolute",
    right: 8,
    top: "50%",
    transform: [{ translateY: -50 }],
    zIndex: 5,
  },
  thumbnailsContainer: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    paddingVertical: 10,
    paddingHorizontal: 5,
  },
  thumbnailWrapper: {
    marginHorizontal: 5,
    padding: 2,
    borderWidth: 2,
    borderColor: "transparent",
    borderRadius: 4,
  },
  activeThumbnail: {
    borderColor: "#00b7f4",
  },
  emptyContainer: {
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
  },
  pageIndicator: {
    marginTop: 8,
    fontSize: 14,
    color: "#666666",
  },
  headerRow: {
    width: "90%",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 10,
    paddingHorizontal: 10,
  },
  headerSide: {
    width: 60,
  },
});

export default TemplateCarousel;
